import React from 'react';
import { IDictionary } from 'models/dictionary';
import { ILicenceSafeStorage } from 'models/licenceConfigurator';
import { IWeaponLicenceOwnerDetailed } from 'models/legalEntity';
import { formatAddress } from 'utils/legalAddress';
import { findInDictionary, IParsedDictionary, parseDictionary } from 'helpers/dictionary';
import { ITableColumns, ITableDataSource } from 'models/table';
import { ILicenceAttachment, ILicencePrintout } from 'models/licence';
import { licencePrintStatusVariant } from 'pages/LicencesPage/helpers';
import { formatDate } from 'utils/date';
import { EButtonVariants } from 'constants/Buttons';
import { ILicenceFirearmsResponse } from 'models/licencesFirearms';
import Button from 'components/atoms/Button';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import { Link } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { StyledRouterLink } from 'components/atoms/RouterLink/styles';
import { getLegalEntityType } from 'components/organisms/RegisteredFirearmDetails';
import { EPermission } from 'models/permissions';
import { appendExtensionToFilename } from 'utils/files';
import { IAccountResponse } from 'models/account';
import { emptyFieldHolder } from 'components/molecules/CardList';
import Tooltip from 'components/atoms/Tooltip';
import { shouldRemoveFirearmFromLicence } from 'utils/firearmValidation';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { hasAccessToLE, hasAccessToLENoVisibilityCheck } from 'utils/access';
import { LETypes } from '../LegalEntityAddTransactionForGroupPage';
import { ELicenceHistoryActions, ELicenceStatusIds, EPaymentStatusId } from '../helpers';

export const getLicenceStatusChipVariant = (status: string): EChipsVariants => {
  if (!status) return EChipsVariants.success;

  switch (status) {
    case ELicenceStatusIds.SUSPENDED:
      return EChipsVariants.error;
    case ELicenceStatusIds.REVOKED:
      return EChipsVariants.error;
    case ELicenceStatusIds.SURRENDERED:
      return EChipsVariants.error;
    case ELicenceStatusIds.EXPIRED:
      return EChipsVariants.warning;
    case ELicenceStatusIds.ACTIVE:
      return EChipsVariants.success;
    case ELicenceStatusIds.SCHEDULED:
      return EChipsVariants.primary;
    case ELicenceStatusIds.CANCELLED:
      return EChipsVariants.inactive;
    default:
      return EChipsVariants.success;
  }
};
export const getLicenceVariant = (status: string): string => {
  if (!status) return '';

  switch (status) {
    case ELicenceStatusIds.SUSPENDED:
      return 'weaponLicenceHistoryAction.suspendLicence.modal.title';
    case ELicenceStatusIds.REVOKED:
      return 'weaponLicenceHistoryAction.revokeLicence.modal.title';
    case ELicenceStatusIds.SURRENDERED:
      return 'weaponLicenceHistoryAction.surrenderLicence.modal.title';
    case ELicenceStatusIds.ACTIVE:
      return 'weaponLicenceHistoryAction.reactivationLicence.modal.title';
    case ELicenceStatusIds.CANCELLED:
      return 'weaponLicenceHistoryAction.cancellationLicence.modal.title';
    default:
      return '';
  }
};

export const getPaymentStatusChipVariant = (paymentStatusId: string): EChipsVariants => {
  if (!paymentStatusId) return EChipsVariants.success;

  switch (paymentStatusId) {
    case EPaymentStatusId.UNPAID:
      return EChipsVariants.error;
    case EPaymentStatusId.PAID:
      return EChipsVariants.success;
    case EPaymentStatusId.REFUNDED:
      return EChipsVariants.success;
    default:
      return EChipsVariants.success;
  }
};

export const isLicenceActive = (status: string | undefined): boolean => {
  if (!status) return false;

  return status === ELicenceStatusIds.ACTIVE;
};

export const isLicenceScheduled = (status: string | undefined): boolean => {
  if (!status) return false;

  return status === ELicenceStatusIds.SCHEDULED;
};

export const isLicenceSuspended = (status: string | undefined): boolean => {
  if (!status) return false;

  return status === ELicenceStatusIds.SUSPENDED;
};

export const createSafeStorageAddressList = (
  safeStorages: ILicenceSafeStorage[],
  country: IDictionary[] | null,
  t: (key: string) => string,
): string => {
  const bulletPoints = safeStorages
    .map((storage) => formatAddress(storage, country))
    .filter((address) => address !== null)
    .map((address) => `• ${address}`)
    .join('\n');

  return bulletPoints || t('legalEntity.noValidAddressesFound.text');
};

export const getOwnerDisplayName = (owner: IWeaponLicenceOwnerDetailed | null): string => {
  if (!owner) return '';

  if (owner.legalType === LETypes.individual) {
    if (owner.individual?.firstName && owner.individual?.lastName) {
      return `${owner.individual.firstName} ${owner.individual.lastName}`;
    }
    return '';
  }
  if (owner.organisation?.organisationName) {
    return owner.organisation.organisationName;
  }
  return '';
};

export const getDictionaryValue = (
  uniqueId: string | undefined | null,
  dictionary: IDictionary[] | null,
): string => {
  if (!uniqueId) return '';
  return findInDictionary(uniqueId, dictionary) || '';
};

export const mapAttachmentsToDataSource = (
  attachments: ILicenceAttachment[],
  attachmentDocumentTypeData: IParsedDictionary,
): ITableDataSource[] =>
  attachments.map((attachment) => ({
    id: attachment.uniqueId,
    name: appendExtensionToFilename(attachment.name, attachment.fileType),
    weaponLicenceDocumentType: attachment.weaponLicenceDocumentType,
    type:
      attachment.type && attachmentDocumentTypeData?.[attachment.type]
        ? attachmentDocumentTypeData[attachment.type]
        : '',
    size: attachment.size,
    fileType: attachment.fileType,
    createdDate: attachment.createdDate,
    comment: attachment.comment,
    addedBy: attachment.addedBy,
  }));

interface IAttachmentsTableColumns {
  handleView: (arg: string) => void;
  handleDownload: (arg: string, name: string) => void;
  showView: boolean;
  shortDateFormat: string;
}

export const attachmentsTableColumns = ({
  handleView,
  handleDownload,
  showView,
  shortDateFormat,
}: IAttachmentsTableColumns): ITableColumns[] => [
  {
    id: 'f0504ca5-6b32-482d-a169-304a6f32f511',
    key: 'name',
    title: 'legalEntity.licenceDetails.attachments.filename.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-a169-304a6f32f512',
    key: 'type',
    title: 'legalEntity.licenceDetails.attachments.type.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-a169-304a6f32f513',
    key: 'comment',
    title: 'legalEntity.licenceDetails.attachments.comment.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-a169-304a6f32f514',
    key: 'createdDate',
    title: 'legalEntity.licenceDetails.attachments.createdDate.column',
    isSortable: false,
    render: (_data, props) =>
      props.createdDate ? formatDate(props.createdDate, shortDateFormat) : '',
  },
  {
    id: 'f0504ca5-6b32-482d-a169-304a6f32f515',
    key: 'addedBy',
    title: 'legalEntity.licenceDetails.attachments.addedBy.column',
    isSortable: false,
  },
  {
    id: 'f0504ca5-6b32-482d-a269-304a6f32f516',
    key: '',
    width: '100px',
    align: 'center',
    title: 'legalEntity.licenceDetails.attachments.actions.column',
    isCenterTableHead: true,
    isSortable: false,
    render: (_data, props) => (
      <div>
        {showView && (
          <Button
            id={`legal_entity_licence_details_attachment_view-button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.view}
            onClick={() => handleView(props.id)}
          />
        )}
        <Button
          id={`legal_entity_licence_details_attachment_download-button-${props.id}`}
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.download}
          onClick={() => handleDownload(props.id, props.name)}
        />
      </div>
    ),
  },
];
export const mapWeaponToDataSource = (
  items: ILicenceFirearmsResponse[],
  state: IDictionary[] | null,
  firearmType: IDictionary[] | null,
  make: IDictionary[] | null,
  model: IDictionary[] | null,
  caliber: IDictionary[] | null,
  firearmAction: IDictionary[] | null,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    firearmUniqueId: item.firearmUniqueId,
    fireCycleId: item.fireCycleId,
    type: findInDictionary(item.typeUniqueId, firearmType),
    make: findInDictionary(item.makeUniqueId, make),
    model: findInDictionary(item.modelUniqueId, model),
    calibre: findInDictionary(item.calibreUniqueId, caliber),
    action: findInDictionary(item.actionUniqueId, firearmAction),
    serialNumber: item.serialNumber,
    state:
      item.stateUniqueId && state ? parseDictionary(state, 'name')[item.stateUniqueId] : '',
    keeper: item.keeper?.name ?? '',
    keeperUniqueId: item.keeper?.uniqueId,
    keeperVisibilityUniqueId: item.keeper?.visibilityUniqueId,
    keeperPrefixGroupUniqueId: item.keeper?.prefixGroupUniqueId,
    keeperOrganisationCategoryUniqueId: item.keeper?.organisationCategoryUniqueId,
    keeperType: item.keeper?.type,
    owner: item.owner?.name ?? '',
    ownerUniqueId: item.owner?.uniqueId,
    ownerVisibilityUniqueId: item.owner?.visibilityUniqueId,
    ownerPrefixGroupUniqueId: item.owner?.prefixGroupUniqueId,
    ownerOrganisationCategoryUniqueId: item.owner?.organisationCategoryUniqueId,
    ownerType: item.owner?.type,
    stateUniqueId: item.stateUniqueId,
    legalityUniqueId: item.legalityUniqueId,
    activityUniqueId: item.activityUniqueId,
  }));

interface IWeaponTableColumns {
  propsLink: string;
  leId: string;
  licenceId: string;
  handleDelete?: (id: string, weaponId: string) => void;
  state?: any;
  hasViewAllFirearmsPermission: boolean;
  hasViewAllLEPermission: boolean;
  account?: IAccountResponse;
  licenceOwnerUniqueId?: string;
  showInfoColumn?: boolean;
  t: (key: string) => string;
}

export const weaponTableColumns = ({
  handleDelete,
  hasViewAllFirearmsPermission,
  hasViewAllLEPermission,
  account,
  licenceOwnerUniqueId,
  showInfoColumn = true,
  t,
}: IWeaponTableColumns): ITableColumns[] => {
  const columns: ITableColumns[] = [];

  if (showInfoColumn) {
    columns.push({
      id: 'f0504ca5-6b32-482d-a169-304a6f32f500',
      key: 'info',
      width: '58px',
      isSortable: false,
      title: t('legalEntity.licenceDetails.weapon.info.column'),
      align: 'center',
      isCenterTableHead: true,
      render: (_data, props) => {
        const shouldRemove = shouldRemoveFirearmFromLicence(
          {
            stateUniqueId: props.stateUniqueId,
            legalityUniqueId: props.legalityUniqueId,
            owner: {
              uniqueId: props.ownerUniqueId,
              visibilityUniqueId: props.ownerVisibilityUniqueId,
              prefixGroupUniqueId: props.ownerPrefixGroupUniqueId,
              type: props.ownerType,
            },
            keeper: props.keeperUniqueId
              ? {
                  uniqueId: props.keeperUniqueId,
                  visibilityUniqueId: props.keeperVisibilityUniqueId,
                  prefixGroupUniqueId: props.keeperPrefixGroupUniqueId,
                  type: props.keeperType,
                }
              : undefined,
          },
          licenceOwnerUniqueId,
          account,
          hasViewAllFirearmsPermission,
          hasViewAllLEPermission,
        );

        return shouldRemove ? (
          <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
            <Tooltip title={t('legalEntity.licenceDetails.weapon.illegalFirearm.tooltip')}>
              <span style={{ display: 'flex', width: '24px', height: '24px' }}>
                <Icon type={EIconTypes.errorWarning} fontSize={24} />
              </span>
            </Tooltip>
          </div>
        ) : null;
      },
    });
  }

  columns.push(
    {
      id: 'f0504ca5-6b32-482d-a169-304a6f32f511',
      key: 'fireCycleId',
      title: 'legalEntity.licenceDetails.weapon.fireCycleId.column',
      isSortable: false,
    },
    {
      id: 'f0504ca5-6b32-482d-a169-304a6f32f512',
      key: 'type',
      title: 'legalEntity.licenceDetails.weapon.type.column',
      isSortable: false,
    },
    {
      id: 'f0504ca5-6b32-482d-a169-304a6f32f513',
      key: 'make',
      title: 'legalEntity.licenceDetails.weapon.make.column',
      isSortable: false,
    },
    {
      id: 'f0504ca5-6b32-482d-a169-304a6f32f515',
      key: 'model',
      title: 'legalEntity.licenceDetails.weapon.model.column',
      isSortable: false,
    },
    {
      id: 'f0504ca5-6b32-482d-a169-304a6f32f516',
      key: 'calibre',
      title: 'legalEntity.licenceDetails.weapon.calibre.column',
      isSortable: false,
    },
    {
      id: 'f0504ca5-6b32-482d-a169-304a6f32f517',
      key: 'action',
      title: 'legalEntity.licenceDetails.weapon.action.column',
      isSortable: false,
    },
    {
      id: 'f0504ca5-6b32-482d-a169-304a6f32f518',
      key: 'serialNumber',
      title: 'legalEntity.licenceDetails.weapon.serialNumber.column',
      isSortable: false,
    },
    {
      id: 'f0504ca5-6b32-482d-a169-304a6f32f519',
      key: 'state',
      title: 'legalEntity.licenceDetails.weapon.state.column',
      isSortable: false,
      align: 'center',
      isCenterTableHead: true,
      width: '135px',
      render: (_data, props) => (
        <Chips
          text={props.state}
          variant={
            renderDataColorByValues(
              props.activityUniqueId,
              props.legalityUniqueId,
              props.stateUniqueId,
            ).stateField
          }
          fullWidth
        />
      ),
    },
    {
      id: 'f0504ca5-6b32-482d-a169-304a6f32f522',
      key: 'owner',
      title: 'legalEntity.licenceDetails.weapon.owner.column',
      isSortable: false,
      render: (_data, props) => (
        <StyledLegalLink
          legalId={props.ownerUniqueId}
          legalType={getLegalEntityType(props.ownerType)}
          labelText={props.owner}
          legalVisibilityUniqueId={props.ownerVisibilityUniqueId}
          legalPrefixGroupUniqueId={props.ownerPrefixGroupUniqueId}
          legalOrganisationCategoryUniqueId={props.ownerOrganisationCategoryUniqueId}
        />
      ),
    },
    {
      id: 'f0504ca5-6b32-482d-a169-304a6f32f521',
      key: 'keeper',
      title: 'legalEntity.licenceDetails.weapon.keeper.column',
      isSortable: false,
      render: (_data, props) => (
        <StyledLegalLink
          legalId={props.keeperUniqueId}
          legalType={getLegalEntityType(props.keeperType)}
          labelText={props.keeper}
          legalVisibilityUniqueId={props.keeperVisibilityUniqueId}
          legalPrefixGroupUniqueId={props.keeperPrefixGroupUniqueId}
          legalOrganisationCategoryUniqueId={props.keeperOrganisationCategoryUniqueId}
        />
      ),
    },
    {
      id: 'f0504ca5-6b32-482d-a269-304a6f32f516',
      key: '',
      width: '100px',
      align: 'center',
      title: 'general.actions.button',
      isCenterTableHead: true,
      isSortable: false,
      render: (_data, props) => (
        <>
          {!handleDelete && (
            <Link
              to={ERouteLinks.ViewRegisteredFirearm.replace(
                ':id',
                props.firearmUniqueId as string,
              )}
            >
              {account &&
                hasAccessToLENoVisibilityCheck(
                  account,
                  props.ownerPrefixGroupUniqueId,
                  props.ownerOrganisationCategoryUniqueId,
                ) &&
                hasAccessToLENoVisibilityCheck(
                  account,
                  props.keeperPrefixGroupUniqueId,
                  props.keeperOrganisationCategoryUniqueId,
                ) && (
                  <Button
                    variant={EButtonVariants.outlined}
                    tableButton
                    icon={EIconTypes.view}
                    permission={[EPermission.VIEW_ALL_FIREARMS]}
                    permissionOr
                  />
                )}
              {account &&
                !hasViewAllFirearmsPermission &&
                hasAccessToLE(
                  account,
                  props.ownerUniqueId,
                  props.ownerVisibilityUniqueId,
                  props.ownerPrefixGroupUniqueId,
                  props.ownerOrganisationCategoryUniqueId,
                ) &&
                hasAccessToLE(
                  account,
                  props.keeperUniqueId,
                  props.keeperVisibilityUniqueId,
                  props.keeperPrefixGroupUniqueId,
                  props.keeperOrganisationCategoryUniqueId,
                ) && (
                  <Button
                    variant={EButtonVariants.outlined}
                    tableButton
                    icon={EIconTypes.view}
                    permission={[EPermission.VIEW_FIREARMS]}
                    permissionOr
                  />
                )}
            </Link>
          )}
          {handleDelete && (
            <Button
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.delete}
              onClick={() => handleDelete(props.firearmUniqueId, props.id)}
            />
          )}
        </>
      ),
    },
  );

  return columns;
};

export const historyTableColumns = (
  longDateFormat: string,
  weaponLicenceStatus: any[],
  t: (key: string) => string,
  handleOpenHistoryDetailsModal: (historyUniqueId: string) => void,
  showDetails: (description: string, externalReason: string, internalReason: string) => void,
): ITableColumns[] => {
  const columns: ITableColumns[] = [
    {
      id: 'a1b2c3d4-5e6f-7890-abcd-123456789001',
      key: 'dateTime',
      width: '15%',
      minWidth: '160px',
      align: 'left',
      title: 'legalEntity.licenceDetails.history.dateTime.column',
      isCenterTableHead: false,
      isSortable: false,
      render: (_data, props) =>
        props.eventDate ? formatDate(props.eventDate, longDateFormat) : '',
    },
    {
      id: 'a1b2c3d4-5e6f-7890-abcd-123456789002',
      key: 'licenceStatus',
      width: '15%',
      minWidth: '160px',
      title: 'legalEntity.licenceDetails.history.licenceStatus.column',
      isCenterTableHead: true,
      isSortable: false,
      render: (_data, props) => {
        const mappedValue = getDictionaryValue(props.statusId, weaponLicenceStatus);
        return (
          <Chips
            text={mappedValue}
            variant={getLicenceStatusChipVariant(props.statusId)}
            fullWidth
          />
        );
      },
    },
    {
      id: 'a1b2c3d4-5e6f-7890-abcd-123456789003',
      key: 'description',
      width: '40%',
      title: 'legalEntity.licenceDetails.history.description.column',
      isSortable: false,
      render: (_data, props) => t(props.description),
    },
    {
      id: 'a1b2c3d4-5e6f-7890-abcd-123456789078',
      key: 'userId',
      width: '10%',
      minWidth: '100px',
      title: 'legalEntity.licenceDetails.history.userId.column',
      align: 'center',
      isCenterTableHead: true,
      isSortable: false,
    },
    {
      id: 'a1b2c3d4-5e6f-7890-abcd-123456789004',
      key: 'user',
      width: '14%',
      minWidth: '160px',
      align: 'center',
      title: 'legalEntity.licenceDetails.history.user.column',
      isCenterTableHead: true,
      isSortable: false,
      render: (_data, props) =>
        props.createdBy ? (
          <StyledRouterLink to={ERouteLinks.ViewUser.replace(':id', props.createdBy)}>
            {props.user}
          </StyledRouterLink>
        ) : (
          emptyFieldHolder(props.user)
        ),
    },
    {
      id: 'a1b2c3d4-5e6f-7890-abcd-123456789005',
      key: 'actions',
      width: '6%',
      minWidth: '100px',
      align: 'center',
      title: 'legalEntity.licenceDetails.history.actions.column',
      isCenterTableHead: true,
      isSortable: false,
      render: (_data, props) => {
        const shouldHideIcon = props.statusId === ELicenceStatusIds.EXPIRED;
        const isCreateAction =
          props.historyStatus &&
          (props.historyStatus === ELicenceHistoryActions.CREATE ||
            props.historyStatus === ELicenceHistoryActions.ACTIVATE);

        if (shouldHideIcon || isCreateAction) {
          return null;
        }

        return (
          <Button
            id={`licence_history_viewDetails-button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.view}
            onClick={() => {
              return [
                ELicenceHistoryActions.REVOKE,
                ELicenceHistoryActions.SURRENDER,
                ELicenceHistoryActions.SUSPEND,
                ELicenceHistoryActions.REACTIVATE,
                ELicenceHistoryActions.REVOKE,
                ELicenceHistoryActions.CANCEL,
              ].includes(props.historyStatus)
                ? showDetails(
                    getLicenceVariant(props.statusId),
                    props.externalReason,
                    props.internalReason,
                  )
                : handleOpenHistoryDetailsModal(props.id);
            }}
          />
        );
      },
    },
  ];

  return columns;
};

export const mapHistoryToDataSource = (historyItems: any[]): ITableDataSource[] =>
  historyItems.map((item) => ({
    id: item.uniqueId,
    user: item.createdByName,
    userId: item.userId,
    createdBy: item.createdBy,
    statusId: item.licenceStatusUniqueId,
    description: item.description,
    eventDate: item.createdDate,
    auditLogUniqueId: item.auditLogUniqueId,
    internalReason: item.internalReason,
    externalReason: item.externalReason,
    historyStatus: item.historyStatus,
  }));

export const mapPrintoutsHistoryToDataSource = (
  t: (key: string) => string,
  printouts: ILicencePrintout[],
  printTypes: IDictionary[] | null,
): ITableDataSource[] =>
  printouts.map((item) => ({
    id: item.printUniqueId,
    status: item.status,
    statusUniqueId: item.statusUniqueId,
    type: findInDictionary(item.typeUniqueId, printTypes),
    typeUniqueId: item.typeUniqueId,
    description: t(item.description),
    user: item.user,
    userId: item.userId,
    userUniqueId: item.userUniqueId,
    eventDate: item.createdDate,
  }));

export const printoutHistoryTableColumns = (longDateFormat: string): ITableColumns[] => [
  {
    id: 'c7f1a2b3-4d5e-6f70-8a9b-1c2d3e4f5001',
    key: 'dateTime',
    width: '15%',
    minWidth: '160px',
    align: 'left',
    title: 'legalEntity.licenceDetails.printoutHistory.dateTime.column',
    isCenterTableHead: false,
    isSortable: false,
    render: (_data, props) =>
      props.eventDate ? formatDate(props.eventDate, longDateFormat) : '',
  },
  {
    id: 'c7f1a2b3-4d5e-6f70-8a9b-1c2d3e4f5002',
    key: 'status',
    width: '15%',
    minWidth: '160px',
    title: 'legalEntity.licenceDetails.printoutHistory.requestStatus.column',
    isCenterTableHead: true,
    isSortable: false,
    render: (_data, props) => (
      <Chips
        text={props.status}
        variant={licencePrintStatusVariant(props.statusUniqueId)}
        fullWidth
      />
    ),
  },
  {
    id: 'c7f1a2b3-4d5e-6f70-8a9b-1c2d3e4f5006',
    key: 'type',
    width: '12%',
    minWidth: '120px',
    title: 'legalEntity.licenceDetails.printoutHistory.type.column',
    isCenterTableHead: true,
    align: 'center',
    isSortable: false,
  },
  {
    id: 'c7f1a2b3-4d5e-6f70-8a9b-1c2d3e4f5003',
    key: 'description',
    width: '33%',
    title: 'legalEntity.licenceDetails.printoutHistory.description.column',
    isSortable: false,
  },
  {
    id: 'c7f1a2b3-4d5e-6f70-8a9b-1c2d3e4f5005',
    key: 'userId',
    width: '10%',
    minWidth: '100px',
    align: 'center',
    title: 'legalEntity.licenceDetails.printoutHistory.userId.column',
    isCenterTableHead: true,
    isSortable: false,
  },
  {
    id: 'c7f1a2b3-4d5e-6f70-8a9b-1c2d3e4f5004',
    key: 'user',
    width: '15%',
    minWidth: '160px',
    align: 'center',
    title: 'legalEntity.licenceDetails.printoutHistory.user.column',
    isCenterTableHead: true,
    isSortable: false,
    render: (_data, props) =>
      props.userUniqueId ? (
        <StyledRouterLink to={ERouteLinks.ViewUser.replace(':id', props.userUniqueId)}>
          {props.user}
        </StyledRouterLink>
      ) : (
        props.user
      ),
  },
];
